import { SeeqNames } from '@seeqdev/constants';
import type { OutputItemMetadataFields } from '@/tools/models/template.types';

/**
 * User-facing widening choice for a derived feature's output item restriction. `narrowest` keeps
 * the compiled/persisted type; `measure` widens Signal/Scalar to the "Signal or Scalar" supertype;
 * `any` opts out entirely (any item type accepted at apply time).
 */
export type OutputTypeWidening = 'narrowest' | 'measure' | 'any';

/**
 * Input-restriction select sentinel: no type restriction, any item type accepted at apply time.
 * Distinct from the {@link OutputTypeWidening} `'any'` member, which is a closed-union value.
 */
export const ANY_INPUT_RESTRICTION = 'any';

/** Input restriction type: the {@link ANY_INPUT_RESTRICTION} sentinel or a concrete item type. */
export type InputRestrictionType = typeof ANY_INPUT_RESTRICTION | (string & {});

/** Wire token used for the Measure widening type — backend OutputItemMetadata.Measure. */
export const MEASURE_OUTPUT_ITEM_TYPE = 'Measure';

const SIGNAL_TYPES = new Set<string>([SeeqNames.FormulaType.Signal, SeeqNames.Types.Signal]);
const SCALAR_TYPES = new Set<string>([SeeqNames.FormulaType.Scalar, SeeqNames.Types.Scalar]);
const CONDITION_TYPES = new Set<string>([SeeqNames.FormulaType.Condition, SeeqNames.Types.Condition]);

/**
 * The metadata-map key under which a given output item type carries its UOM, or `undefined` when
 * the type carries no UOM. Signal (and the Measure widening) use `ValueUom`; Scalar uses `Uom`;
 * Condition and every other/unknown type have no UOM key. This is the single source of truth for
 * the type→UOM-key rule shared by the metadata projection and the read-side label.
 */
export function uomKeyForOutputItemType(itemType: string | undefined): string | undefined {
  if (!itemType) return undefined;
  if (itemType === MEASURE_OUTPUT_ITEM_TYPE || SIGNAL_TYPES.has(itemType)) return SeeqNames.Properties.ValueUom;
  if (SCALAR_TYPES.has(itemType)) return SeeqNames.Properties.Uom;
  return undefined;
}

/** True when the type carries a UOM (Signal, Scalar, or Measure widening). */
export function outputTypeSupportsUom(itemType: string | undefined): boolean {
  return uomKeyForOutputItemType(itemType) !== undefined;
}

/**
 * True when the declared type is concrete enough to validate from metadata alone.
 */
export function supportsMetadataValidation(itemType: string | undefined): boolean {
  if (!itemType) return false;
  return SIGNAL_TYPES.has(itemType) || SCALAR_TYPES.has(itemType) || CONDITION_TYPES.has(itemType);
}

/** True when widening to `Measure` is meaningful (only from Signal/Scalar/Measure, never Condition). */
export function canWidenToMeasure(capturedType: string | undefined): boolean {
  if (!capturedType) return false;
  return SIGNAL_TYPES.has(capturedType) || SCALAR_TYPES.has(capturedType) || capturedType === MEASURE_OUTPUT_ITEM_TYPE;
}

/**
 * Collapses the two naming schemes for the same underlying type onto a single canonical token so
 * they compare equal. The backend feature GET emits SDK item types (`SeeqNames.Types.*` — e.g.
 * "ScalarItem", "Signal"), while formula validation returns formula types (`SeeqNames.FormulaType.*`
 * — e.g. "Scalar", "Signal"). Without this, an unchanged saved feature would re-validate to the
 * formula-type spelling and read as a return-type mismatch against its persisted item-type spelling.
 * Unrecognized types pass through unchanged.
 */
export function normalizeOutputType(itemType: string): string {
  if (SIGNAL_TYPES.has(itemType)) return SeeqNames.FormulaType.Signal;
  if (SCALAR_TYPES.has(itemType)) return SeeqNames.FormulaType.Scalar;
  if (CONDITION_TYPES.has(itemType)) return SeeqNames.FormulaType.Condition;
  return itemType;
}

const NORMALIZABLE_OUTPUT_ITEM_TYPES = new Set<string>([
  SeeqNames.FormulaType.Signal,
  SeeqNames.FormulaType.Scalar,
  SeeqNames.FormulaType.Condition,
]);

/**
 * Normalizes stored/calculated item types to their base formula type.
 */
function toBaseOutputItemType(itemType: string): string {
  switch (itemType) {
    case SeeqNames.Types.StoredSignal:
    case SeeqNames.Types.CalculatedSignal:
    case SeeqNames.Types.Signal:
      return SeeqNames.FormulaType.Signal;
    case SeeqNames.Types.StoredCondition:
    case SeeqNames.Types.CalculatedCondition:
    case SeeqNames.Types.Condition:
      return SeeqNames.FormulaType.Condition;
    case SeeqNames.Types.LiteralScalar:
    case SeeqNames.Types.CalculatedScalar:
    case SeeqNames.Types.Scalar:
      return SeeqNames.FormulaType.Scalar;
    default:
      return itemType;
  }
}

/**
 * Captures the type restriction for a newly-added input without restricting its UOM.
 */
export function captureInputRestrictionType(itemType: string): string | undefined {
  const normalized = normalizeOutputType(toBaseOutputItemType(itemType));
  return NORMALIZABLE_OUTPUT_ITEM_TYPES.has(normalized) ? normalized : undefined;
}

/**
 * True when the latest compile return type can't satisfy the persisted feature's output-type
 * restriction under the current widening choice. `narrowest` requires the same underlying type —
 * aliases are normalized first (see {@link normalizeOutputType}), so the backend GET's "ScalarItem"
 * and formula validation's "Scalar" compare equal and an unchanged feature does not flag a mismatch.
 * `measure` requires both the persisted and compiled types to be widenable to Measure; `any` always
 * accepts. Returns false when there is no persisted restriction (legacy / brand-new feature) or no
 * compile result yet to compare against.
 */
export function isReturnTypeIncompatible(
  existingType: string | undefined,
  newType: string | undefined,
  widening: OutputTypeWidening,
): boolean {
  if (!existingType || !newType) return false;
  if (widening === 'any') return false;
  if (widening === 'measure') {
    return !canWidenToMeasure(existingType) || !canWidenToMeasure(newType);
  }
  return normalizeOutputType(newType) !== normalizeOutputType(existingType);
}

/**
 * Derives the initial widening choice from a persisted feature so the modal opens reflecting the
 * saved restriction:
 *  - `outputItemType === 'Measure'` → `'measure'` (explicit prior widening to the Signal/Scalar
 *    supertype).
 *  - no `outputItemType` → `'any'` — the author explicitly opted out of the type restriction
 *    ("Any item type"), so the choice round-trips. Every feature created through the modal stamps a
 *    concrete type for the `narrowest`/`measure` choices, so an absent type unambiguously means Any.
 *  - any concrete `outputItemType` (Signal/Scalar/Condition/…) → `'narrowest'` (keep that type).
 */
export function initialWideningFromFeature(feature: {
  outputItemType?: string;
  outputItemMetadata?: Record<string, string>;
}): OutputTypeWidening {
  if (feature.outputItemType === MEASURE_OUTPUT_ITEM_TYPE) return 'measure';
  if (!feature.outputItemType) return 'any';
  return 'narrowest';
}

/**
 * Derives the initial UOM-restrict toggle from a persisted feature's metadata map. A missing UOM
 * key (empty map) means the user previously opted out of UOM restriction.
 */
export function initialRestrictUomFromFeature(feature: { outputItemMetadata?: Record<string, string> }): boolean {
  const metadata = feature.outputItemMetadata;
  if (!metadata) return false;
  return metadata[SeeqNames.Properties.ValueUom] !== undefined || metadata[SeeqNames.Properties.Uom] !== undefined;
}

/**
 * Builds output metadata for a type and optional UOM, using {@link uomKeyForOutputItemType}
 * to determine the UOM field.
 */
function projectTypedMetadata(
  targetType: string,
  uom: string | undefined,
  emptyMetadata: Record<string, string> | undefined,
): OutputItemMetadataFields {
  const uomKey = uomKeyForOutputItemType(targetType);
  if (uomKey === undefined || uom === undefined) {
    return { outputItemType: targetType, outputItemMetadata: emptyMetadata };
  }
  return { outputItemType: targetType, outputItemMetadata: { [uomKey]: uom } };
}

/**
 * Projects a user's widening + UOM-restrict choices and the compiled/captured metadata into the
 * wire shape used by `DerivedFeatureV1`. The compiled type and metadata come from a successful
 * `validateFormula` call (or from the persisted feature on first open).
 *
 *  - `any` widening → `{ outputItemType: undefined, outputItemMetadata: undefined }` (full opt-out).
 *  - `measure` widening → `outputItemType: 'Measure'`; uses the `ValueUom` key for UOM (per backend
 *    `OutputItemMetadata.Measure.toMetadataMap()`).
 *  - `narrowest` widening → use the compiled type and the matching UOM key (Signal → `ValueUom`,
 *    Scalar → `Uom`, Condition → no UOM).
 *  - When `restrictUom` is false and the selected type carries UOM, the UOM key is dropped from
 *    the metadata map (becomes `{}`, "any UOM").
 *
 * Returns `{ outputItemType: undefined, outputItemMetadata: undefined }` when there is no
 * compiled type and the user did not explicitly opt out — i.e. the caller should treat this as
 * "no metadata to send" and avoid overwriting a persisted restriction.
 */
export function projectOutputMetadata({
  widening,
  restrictUom,
  capturedType,
  capturedMetadata,
}: {
  widening: OutputTypeWidening;
  restrictUom: boolean;
  capturedType: string | undefined;
  capturedMetadata: Record<string, string> | undefined;
}): OutputItemMetadataFields {
  if (widening === 'any') {
    return { outputItemType: undefined, outputItemMetadata: undefined };
  }

  if (!capturedType) {
    return { outputItemType: undefined, outputItemMetadata: undefined };
  }

  const targetType = widening === 'measure' ? MEASURE_OUTPUT_ITEM_TYPE : capturedType;
  const capturedUom = restrictUom
    ? (capturedMetadata?.[SeeqNames.Properties.ValueUom] ?? capturedMetadata?.[SeeqNames.Properties.Uom])
    : undefined;

  return projectTypedMetadata(targetType, capturedUom, {});
}

/**
 * Projects an input feature's type and UOM restrictions into the `InputFeatureV1` wire shape.
 * Unlike {@link projectOutputMetadata}, {@link ANY_INPUT_RESTRICTION} represents no restriction.
 */
export function buildInputRestrictionFields(
  type: InputRestrictionType,
  uom: string | undefined,
): OutputItemMetadataFields {
  if (type === ANY_INPUT_RESTRICTION) {
    return { outputItemType: undefined, outputItemMetadata: undefined };
  }

  // Type-only input restrictions omit the metadata map.
  return projectTypedMetadata(type, uom, undefined);
}
